import type React from "react";
import { Group, Modal, Stack, Text } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";

interface UnsavedChangesModalProps {
  /** Row numbers (`rowIndex`) whose edits are not saved; null closes it. */
  unsavedRows: number[] | null;
  onClose: () => void;
  onRetry: () => void;
  onDiscard: () => void;
}

const MAX_LISTED = 10;

/**
 * Shown when an Excel export is asked for while some row edits are still not
 * saved. The file is generated from the server's copy, so the reviewer picks:
 * retry the saves and export, or drop the edits and export what is saved.
 */
const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  unsavedRows,
  onClose,
  onRetry,
  onDiscard,
}) => {
  const { t } = useTranslation();
  const rows = unsavedRows ?? [];
  const listed = rows
    .slice()
    .sort((a, b) => a - b)
    .slice(0, MAX_LISTED)
    .map((rowIndex) => `#${rowIndex + 1}`)
    .join(", ");

  return (
    <Modal
      opened={unsavedRows !== null}
      onClose={onClose}
      centered
      title={t("jobResultsPage.export.unsavedTitle", {
        defaultValue: "Some changes are not saved",
      })}
    >
      <Stack gap="sm">
        <Text size="sm">
          {t("jobResultsPage.export.unsaved", {
            count: rows.length,
            defaultValue:
              "{{count}} row change(s) could not be saved. The Excel file is built from saved data, so they would be missing from it.",
          })}
        </Text>
        <Text size="sm" c="dimmed">
          {listed}
          {rows.length > MAX_LISTED && ` +${rows.length - MAX_LISTED}`}
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t("jobResultsPage.export.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button variant="light" color="red" onClick={onDiscard}>
            {t("jobResultsPage.export.discardAndExport", {
              defaultValue: "Discard changes and export",
            })}
          </Button>
          <Button onClick={onRetry}>
            {t("jobResultsPage.export.retryAndExport", {
              defaultValue: "Retry saving and export",
            })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default UnsavedChangesModal;
